import { useEnvironmentVariables } from "#app/EnvironmentProvider.tsx";
import { ConnectorChannelIcon } from "#components/ui/ConnectorChannelIcon.tsx";
import { useGetChannelsDetailsSuspenseQuery } from "#features/channels/api/useGetChannelsDetailsSuspenseQuery.ts";
import { useDisabledChannelsStore } from "#features/channels/stores/useDisabledChannelsStore.ts";
import { useConversationSkillsQuery } from "#features/conversations/api/useConversationSkillsQuery.ts";
import { COMPOSER_TRAY_OFFSET, ComposerTrayItem, ComposerTrayMenu, ComposerTrayPopover, ComposerTraySection, useComposerTrayWidth } from "#features/conversations/components/conversation-input/ComposerTray.tsx";
import type { SlashMenuHandle } from "#features/conversations/components/conversation-input/composerEditor.ts";
import { SkillIcon } from "#features/conversations/components/conversation-input/SkillIcon.tsx";
import { localizeSkill, searchSkills, toSkillLanguage } from "#features/conversations/components/conversation-input/skillSearch.ts";
import { useConnectIntegration } from "#features/conversations/hooks/useConnectIntegration.ts";
import { useWlAppLinks } from "#features/conversations/hooks/useWlAppLinks.ts";
import type { EditorListbox } from "#features/conversations/components/conversation-input/mentionSuggestion.ts";
import { channelTypeToIntegration } from "#features/conversations/integrations/index.ts";
import { useComposerTrayStore } from "#features/conversations/stores/useComposerTrayStore.ts";
import { channels } from "#features/conversations/types/channels.tsx";
import { WL_APP_CONNECTED_APPS_PATH } from "#features/conversations/utils/wlAppPaths.ts";
import { useConnectedExternalApps } from "#hooks/useConnectedExternalApps.ts";
import { useFeatureFlag } from "#hooks/useFeatureFlag.ts";
import { useFileDialog } from "#hooks/useFileDialog.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import type { ConnectorChannelType, Skill } from "#types/types.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { Button } from "@hopper-ui/components";
import { IconCheck, IconPaperclip45, IconPencilBox, IconPlus } from "@intentui/icons";
import { useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, type Key, type RefObject } from "react";
import { MenuTrigger } from "react-aria-components";
import { useParams } from "react-router";
import "./ConversationComposerMenu.css";

interface Props {
    isAttachDisabled: boolean;
    isSkillSelectDisabled: boolean;
    // The text after a "/" typed in the input, or null: while set, the tray opens over the input filtered by it.
    slashQuery: string | null;
    slashMenuRef: RefObject<SlashMenuHandle | null>;
    handleFileSelected: (selectedFiles: File[]) => void;
    onSkillSelect: (skill: Skill) => void;
    // Closes the tray opened by "/" and hands focus back to the input; clearFragment also drops the typed "/query".
    onSlashClose: (clearFragment: boolean) => void;
    focusInput: () => void;
    // The tray opened by "/" and its active row, for the editor to name while focus stays there; null once it closes.
    onSlashListboxChange: (listbox: EditorListbox | null) => void;
    // The bar the tray opens above, edge to edge with it.
    trayAnchorRef: RefObject<HTMLElement | null>;
}

const ATTACH_KEY = "attach";
const CONNECT_MORE_KEY = "connect-more";
const CUSTOMIZE_SKILLS_KEY = "customize-skills";
const APP_KEY_PREFIX = "app:";
const CONNECT_KEY_PREFIX = "connect:";
const SKILL_KEY_PREFIX = "skill:";
// Microsoft's own short names, for the tray only; the shared channel names keep "Microsoft" everywhere else.
const TRAY_NAMES: Partial<Record<ConnectorChannelType, string>> = {
    UserOutlook: "Outlook",
    UserTeams: "Teams"
};

// Workleap's own sources have no integration manifest, so their tray lines live here.
const WORKLEAP_DESCRIPTION_KEYS: Partial<Record<ConnectorChannelType, string>> = {
    WorkleapOfficevibe: "composerTrayDescriptions.officevibe",
    WorkleapPerformance: "composerTrayDescriptions.performance",
    WebCrawler: "composerTrayDescriptions.webCrawler"
};

// The "+", or a "/" typed in the input, opens a tray over the bar: files to add, every connected app with its
// state, the apps to connect, then the skills. Opened by "/", the text after it filters the rows and focus stays in the input.
export function ConversationComposerMenu({ isAttachDisabled, isSkillSelectDisabled, slashQuery, slashMenuRef, handleFileSelected, onSkillSelect, onSlashClose, focusInput, onSlashListboxChange, trayAnchorRef }: Props) {
    const { tShared, i18n } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const { hasFeatureFlag } = useFeatureFlag();
    const isSharePointEnabled = hasFeatureFlag("SharePointUserIntegration");
    const isSkillsEnabled = hasFeatureFlag("SlashCommandPicker");
    const skills = useConversationSkillsQuery(isSkillsEnabled);
    const skillLanguage = toSkillLanguage(i18n.language);
    const localizedSkills = useMemo(
        () => skills.map(skill => localizeSkill(skill, skillLanguage)),
        [skills, skillLanguage]
    );
    const { allApps } = useConnectedExternalApps();
    const { data: { personalIntegrations } } = useGetChannelsDetailsSuspenseQuery();
    const connectIntegration = useConnectIntegration();
    const disabledChannels = useDisabledChannelsStore(state => state.disabledChannels);
    const toggleChannel = useDisabledChannelsStore(state => state.toggleChannel);
    const { fileInputRef, open: openFileDialog, onChange } = useFileDialog({ onFilesSelected: handleFileSelected });
    const { wlAppBaseUrl } = useEnvironmentVariables();
    const { conversationId } = useParams<{ conversationId: string }>();
    const [isOpen, setOpen] = useState(false);
    const isSlashActive = slashQuery !== null && !isOpen;
    const query = isSlashActive ? slashQuery : "";
    const [activeIndex, setActiveIndex] = useState(0);
    // State, not a ref: the popover mounts after the tray opens, and the effect below must run again once its rows exist.
    const [slashPopover, setSlashPopover] = useState<HTMLElement | null>(null);
    const trayId = useId();
    // A switch in place: React Aria closes a menu after any action, so the tray keeps itself open after an app toggle.
    const isTogglingRef = useRef(false);
    const setTrayOpen = useComposerTrayStore(state => state.setTrayOpen);
    const trackingLocation = conversationId ? "Conversation" : "Home Page";
    const connectMoreHref = `${wlAppBaseUrl}${WL_APP_CONNECTED_APPS_PATH}`;
    const { skillsPageHref: customizeSkillsHref } = useWlAppLinks();

    // Only apps this build can draw; SharePoint waits for its flag, as in the side panel.
    const connectableApps = useMemo(() => personalIntegrations
        .filter(({ channelType, isConnected, canConnect }) => !isConnected && canConnect && channelTypeToIntegration[channelType])
        .filter(({ channelType }) => isSharePointEnabled || channelType !== "UserSharepoint")
        .map(({ channelType }) => channelType), [isSharePointEnabled, personalIntegrations]);

    const nameOf = useCallback((channelType: ConnectorChannelType) => {
        const channel = channels[channelType];

        return TRAY_NAMES[channelType] ?? (channel ? tShared(channel.nameKey) : channelType);
    }, [tShared]);

    const descriptionOf = useCallback((channelType: ConnectorChannelType) => {
        const key = channelTypeToIntegration[channelType]?.trayDescriptionKey ?? WORKLEAP_DESCRIPTION_KEYS[channelType];

        return key ? tShared(key) : undefined;
    }, [tShared]);

    // A "/" is for skills, as an "@" is for people; attaching files and the connected apps stay in the "+" tray.
    const showsPlusRows = !isSlashActive;
    const attachLabel = tShared("attachFiles");
    const connectMoreLabel = tShared("connectMoreApps");
    const visibleSkills = useMemo(
        () => isSkillsEnabled ? searchSkills(localizedSkills, query) : [],
        [isSkillsEnabled, localizedSkills, query]
    );
    const visibleApps = showsPlusRows ? allApps : [];
    const visibleConnectableApps = showsPlusRows ? connectableApps : [];
    // An action, not a match: it follows the skills whatever the query, and a "/" with no skill still closes the tray.
    // Only where there's a wl-app skills page to open.
    const isCustomizeSkillsVisible = customizeSkillsHref !== undefined && isSlashActive && visibleSkills.length > 0;
    // A "/" that matches no skill leaves the tray closed, so the input reads as plain text.
    const isSlashOpen = isSlashActive && visibleSkills.length > 0;
    const trayWidth = useComposerTrayWidth(trayAnchorRef, isOpen || isSlashOpen);

    // The panel content fades behind the open tray; it comes back if the bar unmounts with the tray open.
    useEffect(() => {
        setTrayOpen(isOpen || isSlashOpen);

        return () => setTrayOpen(false);
    }, [isOpen, isSlashOpen, setTrayOpen]);

    const skillKey = (slug: string) => `${SKILL_KEY_PREFIX}${slug}`;
    const disabledKeys = [
        ...(isAttachDisabled ? [ATTACH_KEY] : []),
        ...(isSkillSelectDisabled ? visibleSkills.map(({ slug }) => skillKey(slug)) : [])
    ];
    // The "/" tray's rows in order, as the keyboard walks them while focus stays in the input.
    const slashKeys = [
        ...visibleSkills.map(({ slug }) => skillKey(slug)),
        ...(isCustomizeSkillsVisible ? [CUSTOMIZE_SKILLS_KEY] : [])
    ].filter(key => !disabledKeys.includes(key));
    const activeSlashIndex = Math.min(activeIndex, slashKeys.length - 1);
    const activeSlashKey = isSlashOpen ? slashKeys[activeSlashIndex] : undefined;

    useEffect(() => {
        setActiveIndex(0);
    }, [slashQuery]);

    useEffect(() => {
        // React Aria gives each row its own DOM id (the row's `id` is only its key, in data-key), so the editor points at the
        // rendered one. Found by key: the active class lands a render after the key changes.
        const activeRow = activeSlashKey ? [...slashPopover?.querySelectorAll("[data-key]") ?? []].find(row => row.getAttribute("data-key") === activeSlashKey) : undefined;
        activeRow?.scrollIntoView?.({ block: "nearest" });
        onSlashListboxChange(isSlashOpen ? { id: trayId, activeOptionId: activeRow?.id || undefined } : null);
    }, [activeSlashKey, isSlashOpen, slashPopover, trayId, onSlashListboxChange]);

    const handleOpenChange = useCallback((open: boolean) => {
        if (!open && isTogglingRef.current) {
            isTogglingRef.current = false;

            return;
        }
        setOpen(open);
        if (open) {
            trackEvent(MixpanelEvent.ButtonClicked, { Location: trackingLocation, Trigger: "Toggle Knowledge Sources" });
        }
    }, [trackEvent, trackingLocation]);

    const handleAction = (key: Key) => {
        const id = String(key);
        if (id === ATTACH_KEY) {
            openFileDialog();
        } else if (id.startsWith(SKILL_KEY_PREFIX)) {
            const skill = visibleSkills.find(({ slug }) => skillKey(slug) === id);
            if (skill) {
                onSkillSelect(skill);
            }

            return;
        } else if (id === CONNECT_MORE_KEY) {
            trackEvent(MixpanelEvent.ButtonClicked, { Location: trackingLocation, Trigger: "Connect More Sources" });
        } else if (id.startsWith(APP_KEY_PREFIX)) {
            isTogglingRef.current = !isSlashOpen;
            toggleChannel(id.slice(APP_KEY_PREFIX.length) as ConnectorChannelType);
        } else if (id.startsWith(CONNECT_KEY_PREFIX)) {
            const manifest = channelTypeToIntegration[id.slice(CONNECT_KEY_PREFIX.length) as ConnectorChannelType];
            if (manifest) {
                connectIntegration({ type: "integration", provider: manifest.key });
            }
        }

        if (isSlashOpen) {
            if (id.startsWith(APP_KEY_PREFIX)) {
                focusInput();
            } else {
                onSlashClose(true);
            }
        }
    };

    useImperativeHandle(slashMenuRef, () => ({
        handleKeyDown: ({ key, isComposing }) => {
            // IME composition (CJK etc.) must reach the editor so Enter can confirm the composed text.
            if (isComposing || !activeSlashKey) {
                return false;
            }
            switch (key) {
                case "ArrowDown":
                    setActiveIndex((activeSlashIndex + 1) % slashKeys.length);

                    return true;
                case "ArrowUp":
                    setActiveIndex((activeSlashIndex - 1 + slashKeys.length) % slashKeys.length);

                    return true;
                case "Enter":
                case "Tab":
                    // A press on the link row opens it itself; from the keyboard the tray does.
                    if (activeSlashKey === CONNECT_MORE_KEY) {
                        window.open(connectMoreHref, "_blank", "noopener");
                    } else if (activeSlashKey === CUSTOMIZE_SKILLS_KEY) {
                        window.open(customizeSkillsHref, "_blank", "noopener");
                    }
                    handleAction(activeSlashKey);

                    return true;
                default:
                    return false;
            }
        }
    }));

    const connectLabel = tShared("connect");
    const offLabel = tShared("connectedAppOff");
    const rowProps = (key: string) => isSlashOpen
        ? { isActive: key === activeSlashKey, match: query, onPointerMove: () => setActiveIndex(Math.max(slashKeys.indexOf(key), 0)) }
        : {};

    const menu = (
        <ComposerTrayMenu
            id={trayId}
            aria-label={tShared("addToMessage")}
            disabledKeys={disabledKeys}
            onAction={handleAction}
        >
            {showsPlusRows && (
                <ComposerTraySection title={tShared("composerTrayAdd")}>
                    <ComposerTrayItem
                        id={ATTACH_KEY}
                        name={attachLabel}
                        description={tShared("attachFilesDescription")}
                        icon={<IconPaperclip45 />}
                        {...rowProps(ATTACH_KEY)}
                    />
                </ComposerTraySection>
            )}
            {showsPlusRows && (
                <ComposerTraySection title={tShared("connectedApps")}>
                    {visibleApps.map(channelType => {
                        const isOn = !disabledChannels.includes(channelType);

                        return (
                            <ComposerTrayItem
                                key={channelType}
                                id={`${APP_KEY_PREFIX}${channelType}`}
                                name={nameOf(channelType)}
                                description={descriptionOf(channelType)}
                                icon={<ConnectorChannelIcon type={channelType} isPlateless width="core_160" height="core_160" alt="" />}
                                status={isOn ? <IconCheck /> : offLabel}
                                statusLabel={isOn ? undefined : offLabel}
                                {...rowProps(`${APP_KEY_PREFIX}${channelType}`)}
                            />
                        );
                    })}
                    {visibleConnectableApps.map(channelType => (
                        <ComposerTrayItem
                            key={channelType}
                            id={`${CONNECT_KEY_PREFIX}${channelType}`}
                            name={nameOf(channelType)}
                            description={descriptionOf(channelType)}
                            icon={<ConnectorChannelIcon type={channelType} isPlateless width="core_160" height="core_160" alt="" />}
                            status={connectLabel}
                            statusLabel={connectLabel}
                            {...rowProps(`${CONNECT_KEY_PREFIX}${channelType}`)}
                        />
                    ))}
                    {showsPlusRows && (
                        <ComposerTrayItem
                            id={CONNECT_MORE_KEY}
                            name={connectMoreLabel}
                            icon={<IconPlus />}
                            statusLabel={tShared("opensInNewTab")}
                            href={connectMoreHref}
                            isQuiet
                            {...rowProps(CONNECT_MORE_KEY)}
                        />
                    )}
                </ComposerTraySection>
            )}
            {visibleSkills.length > 0 && (
                <ComposerTraySection title={tShared("composerTraySkills")}>
                    {visibleSkills.map(skill => (
                        <ComposerTrayItem
                            key={skill.slug}
                            id={skillKey(skill.slug)}
                            name={skill.title}
                            description={skill.description}
                            icon={<SkillIcon />}
                            {...rowProps(skillKey(skill.slug))}
                        />
                    ))}
                    {isCustomizeSkillsVisible && (
                        <ComposerTrayItem
                            id={CUSTOMIZE_SKILLS_KEY}
                            name={tShared("customizeSkills")}
                            icon={<IconPencilBox />}
                            statusLabel={tShared("opensInNewTab")}
                            href={customizeSkillsHref}
                            isQuiet
                            {...rowProps(CUSTOMIZE_SKILLS_KEY)}
                        />
                    )}
                </ComposerTraySection>
            )}
        </ComposerTrayMenu>
    );

    return (
        <>
            <MenuTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
                <Button variant="ghost-secondary" size="sm" borderRadius="circle" aria-label={tShared("addToMessage")} className="wai-composer-ghost wai-composer-plus">
                    <IconPlus />
                </Button>
                <ComposerTrayPopover anchorRef={trayAnchorRef} placement="top" offset={COMPOSER_TRAY_OFFSET} inlineSize={trayWidth}>
                    {menu}
                </ComposerTrayPopover>
            </MenuTrigger>
            {isSlashOpen && (
                <ComposerTrayPopover
                    anchorRef={trayAnchorRef}
                    placement="top"
                    offset={COMPOSER_TRAY_OFFSET}
                    inlineSize={trayWidth}
                    popoverRef={setSlashPopover}
                    standalone={{ isOpen: true, onOpenChange: open => !open && onSlashClose(false) }}
                >
                    {menu}
                </ComposerTrayPopover>
            )}
            <input ref={fileInputRef} type="file" multiple hidden onChange={onChange} />
        </>
    );
}
